import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { Button } from 'lego-on-react';
import { flatten } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { TestBem } from '../../service/TestBem';
import { RateCorrectionConditionChooserItemsGroup } from '../../typings/ratesCorrections';
import {
    RateCorrectionConditionChooser,
    RateCorrectionConditionChooserSearchFilterParams,
    RateCorrectionConditionChooserView
} from '../RateCorrectionConditionChooser/RateCorrectionConditionChooser';
import { ScopedPopup } from '../ScopedPopup/ScopedPopup';
import { Shape2, Shape2Icon } from '../Shape2/Shape2';

import './RateCorrectionConditionItemsAppender.css';
import * as keyset from './RateCorrectionConditionItemsAppender.i18n';

const i18n = i18nFactory(keyset);

export interface RateCorrectionConditionChooserState {
    isOpen: boolean;
    currentValue: Array<string>;
}

export interface RateCorrectionConditionChooserProps {
    cls?: string;
    items: Array<RateCorrectionConditionChooserItemsGroup>;
    value?: Array<string>;
    disabledItems?: Array<string>;
    hideDisabledItems?: boolean;
    maxSelectableItemsNumber?: number;
    checkAllControl?: {
        label: string;
    };
    searchFilter?: RateCorrectionConditionChooserSearchFilterParams;
    isDisabled?: boolean;
    onChange: (values: Array<string>) => void;
}

const blockClass = 'rate-correction-condition-items-appender';

export class RateCorrectionConditionItemsAppender extends React.PureComponent<
    RateCorrectionConditionChooserProps,
    RateCorrectionConditionChooserState
> {
    static defaultProps = {
        value: []
    };

    private switcher = React.createRef<HTMLDivElement>();

    componentDidUpdate(prevProps: RateCorrectionConditionChooserProps, prevState: RateCorrectionConditionChooserState) {
        if (prevState.isOpen !== this.state.isOpen && this.state.isOpen === true) {
            this.setState({
                currentValue: this.props.value!
            });
        }
    }

    constructor(props: RateCorrectionConditionChooserProps) {
        super(props);

        this.state = {
            isOpen: false,
            currentValue: props.value!
        };
    }

    render() {
        const {
            cls,
            items,
            checkAllControl,
            disabledItems,
            hideDisabledItems,
            searchFilter,
            maxSelectableItemsNumber
        } = this.props;
        const { isOpen, currentValue } = this.state;

        return (
            <div className={classNames(Bem.cls(blockClass), cls)} ref={this.switcher}>
                {this.getOpener()}

                <ScopedPopup
                    hiding={true}
                    theme="normal"
                    directions={['top-left']}
                    target={'anchor'}
                    anchor={this.switcher.current}
                    visible={isOpen}
                    onOutsideClick={this.closePopup}
                >
                    <RateCorrectionConditionChooser
                        items={items}
                        disabledItems={disabledItems}
                        hideDisabledItems={hideDisabledItems}
                        maxSelectableItemsNumber={maxSelectableItemsNumber}
                        value={currentValue}
                        view={RateCorrectionConditionChooserView.withCheckers}
                        multiSelect={true}
                        checkAllControl={checkAllControl}
                        onChange={this.onCorrectionChooserChange}
                        searchFilter={searchFilter}
                    />

                    {this.getFooter()}
                </ScopedPopup>
            </div>
        );
    }

    private onCorrectionChooserChange = (value: Array<string>) => {
        this.setState({
            currentValue: value
        });
    };

    private getOpener = () => {
        const { isDisabled } = this.props;

        return (
            <Button
                cls={TestBem.cls(blockClass, 'add-btn')}
                theme="normal"
                view="classic"
                size="s"
                onClick={this.openPopup}
                disabled={isDisabled}
            >
                <Shape2
                    cls={Bem.cls(blockClass, 'btn-icon')}
                    width={11}
                    height={11}
                    icon={Shape2Icon.plus}
                    hoverable={true}
                />
                {i18n('add-correction')}
            </Button>
        );
    };

    private isSelectedMoreThanMax = () => {
        const { maxSelectableItemsNumber } = this.props;
        const selectedValuesLength = this.state.currentValue.length;

        if (maxSelectableItemsNumber === undefined) {
            return false;
        }

        return selectedValuesLength > maxSelectableItemsNumber;
    };

    private getFooter = () => {
        const selectedValuesLength = this.state.currentValue.length;

        return (
            <div className={Bem.cls(blockClass, 'footer')}>
                <Button
                    cls={TestBem.cls(blockClass, 'append-btn')}
                    theme="normal"
                    view="classic"
                    size="s"
                    disabled={selectedValuesLength === 0 || this.isSelectedMoreThanMax()}
                    onClick={this.onAddButtonClick}
                >
                    {selectedValuesLength === 0
                        ? i18n('add-button-text')
                        : i18n('add-button-text-with-count', {
                              count: selectedValuesLength
                          })}
                </Button>
            </div>
        );
    };

    private openPopup = () => {
        this.setState({
            isOpen: true
        });
    };

    private closePopup = () => {
        this.setState({
            isOpen: false
        });
    };

    private onAddButtonClick = () => {
        const { onChange } = this.props;
        const { currentValue } = this.state;

        onChange(flatten(currentValue));

        this.closePopup();
    };
}
